import cloneDeep from 'lodash/cloneDeep';

import Joi from '@hapi/joi';
import { toBooleanString } from 'utils/import/columns';
import { codeToString, sameCode } from './codes';

const schema = {
  // The file's own header, not the API's. importedData keeps the shape it was
  // read in, and the duck translates on the way out.
  // Text, like the column. It was Joi.number(), which refused the 28 suppliers
  // whose codes are not digits - FD013134, 200XXXX, XXXX, and EGEO has 25 of
  // them. No min: 191 supplier codes are one or two characters, EGEO's "E0"
  // among them, so cod_mp's min(3) would lock them out instead. No pattern
  // either - none of the 1103 codes needs it today and it is a latent refusal
  // of the first code somebody writes with a dot in it.
  cod_fornecedor: Joi.string().max(60).required(),
  fornecedor: Joi.string().min(3).max(250).required(),
  // sanitize() has already folded every spelling a person might write - Sim,
  // não, TRUE, and the raw booleans the template contains - into 'true' or
  // 'false'. Anything it did not recognise arrives here untouched and is
  // refused by name, which a plain string() did not do: it took any text at
  // all, so `talvez` sailed past and failed at the API instead, against a line
  // number rather than a column.
  ativo: Joi.string().valid('true', 'false').required()
};

const rootSchema = Joi.array().min(1)
  .items(Joi.object(schema))
  .required();

export const validate = (data, suppliers) => {
  const validation = rootSchema.validate(data, { abortEarly: false });

  // What the imported file holds, as against `suppliers`, which is what the
  // account already has. `v.fornecedor` and `v.cod_fornecedor` keep their names:
  // those are the spreadsheet's column headers, not ours to rename.
  const importedNames = [];
  const importedCodes = [];
  data.forEach(v => {
    importedNames.push(v.fornecedor);
    importedCodes.push(v.cod_fornecedor);
  });


  const error = (entity, index, value, msg)  => ({
    context: {
      key: entity,
      label: `[${index}].${entity} repetido`,
      value: value
    },
    message: `"${entity}" ${msg ? msg : ' valor repetido!'}`
  });

  const logRepetitions = (data, entity) => {
    const auxData = [...data];
    for (let i = 0; i < data.length; i += 1) {
      auxData.shift();
      const index = auxData.indexOf(data[i]);
      if (index !== -1) {
        const iError = error(entity,i + index + 1, data[i]);
        if (validation.error) {
          validation.error.details.push(iError);
        } else {
          validation.error = { details: [iError] };
        }
      }
    }
  };

  // Verify repetitions inside imported file
  logRepetitions(importedNames, 'fornecedor');
  logRepetitions(importedCodes, 'codigo fornecedor');

  // Verify repetitions against existing suppliers
  suppliers.forEach(sup => {
    data.forEach( (newSup, index) => {
      if (sup.name === newSup.fornecedor) {
        const iError = error('fornecedor', index, newSup.fornecedor,' existe na BD de Fornecedores');
        if (validation.error) {
          validation.error.details.push(iError);
        } else {
          validation.error = { details: [iError] };
        }
      }
      // `cod_fornecedor` on purpose: this runs on the file's own rows, before
      // toApiPayload renames anything. That header belongs to the customer's
      // spreadsheet and is not ours to translate.
      if (sameCode(sup.code, newSup.cod_fornecedor)) {
        const iError = error('codigo fornecedor', index, newSup.cod_fornecedor,' existe na BD de Fornecedores');
        if (validation.error) {
          validation.error.details.push(iError);
        } else {
          validation.error = { details: [iError] };
        }
      }
    });
  });
  return validation;
};

export const sanitize = (data) => {
  let sanitizedData = cloneDeep(data);
  let sanitizeOptions = {
    eliminateEmptyRows: true,
    ativoToBool: true
  };
  if( sanitizeOptions.eliminateEmptyRows) {
    sanitizedData = sanitizedData.filter(row => {
      const props = Object.keys(row);
      const isNotRow = props.map(prop => row[prop] === undefined).every(v => v);
      return isNotRow ? undefined : row;
    });
  }

  // Transform ativo to Bool
  if (sanitizeOptions.ativoToBool) {
    sanitizedData.forEach(row => {
      row.ativo = toBooleanString(row.ativo);
    });
  }

  // The code is text, and Excel does not always agree: the template we hand out
  // stores these as numeric cells, so most files arrive with a JS number here.
  // Normalising before the schema is what lets it ask for a string without
  // refusing every numeric code - and it is also what makes logRepetitions
  // above work, since indexOf compares 777 and '777' as different values.
  sanitizedData.forEach(row => {
    row.cod_fornecedor = codeToString(row.cod_fornecedor);
  });

  return sanitizedData;
};

export default validate;
